import { Globe, Menu, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import { LOCALE_NAMES } from '../i18n/config';
import { useI18n } from '../i18n/I18nProvider';
import LanguageSelector from './LanguageSelector';
import { Button } from './ui/button';

interface HeaderProps {
  leanpubUrl: string;
}

const Header: React.FC<HeaderProps> = ({  leanpubUrl }) => {
  const { t, locale } = useI18n();
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const handleAmazonClick = () => {
    ReactGA.event({
      category: 'Clic',
      action: 'Botón Amazon',
      label: 'Header',
    });
  };

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 10);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-30 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}
    >
      <div className='container mx-auto px-4 sm:px-6 lg:px-8'>
        <div className='flex items-center justify-between h-16'>
          <div className='flex items-center'>
            <a href='#' className='text-primary font-heading font-bold text-xl'>
              Angular Testing
            </a>
          </div>

          {/* Desktop menu */}
          <nav className='hidden md:flex space-x-8'>
            <a href='#reality' className='text-foreground hover:text-primary transition-colors'>
              {t.navigation.reality}
            </a>
            <a href='#features' className='text-foreground hover:text-primary transition-colors'>
              {t.navigation.features}
            </a>
            <a href='#chapters' className='text-foreground hover:text-primary transition-colors'>
              {t.navigation.chapters}
            </a>
            <a href='/blog' className='text-foreground hover:text-primary transition-colors'>
              {t.navigation.blog}
            </a>
            {/* <a href='#videos' className='text-foreground hover:text-primary transition-colors'>
              {t.navigation.videos || 'Videos'}
            </a> */}
          </nav>

          <div className='hidden md:flex items-center space-x-4'>
            <LanguageSelector />
            <Button asChild className='btn-primary'>
              <a href={leanpubUrl} target='_blank' rel='noopener noreferrer' onClick={handleAmazonClick}>
                {t.all.ctaButton}
              </a>
            </Button>
          </div>

          {/* Mobile menu button */}
          <div className='md:hidden flex items-center'>
            <LanguageSelector />
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className='ml-2 inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-primary focus:outline-none'
              aria-label={menuOpen ? 'Cerrar menú' : 'Abrir menú'}
            >
              {menuOpen ? (
                <X className='block h-6 w-6' aria-hidden='true' />
              ) : (
                <Menu className='block h-6 w-6' aria-hidden='true' />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div className={`md:hidden ${menuOpen ? 'block' : 'hidden'} bg-white shadow-lg`}>
        <div className='px-2 pt-2 pb-3 space-y-1 sm:px-3'>
          <a
            href='#reality'
            className='block px-3 py-2 rounded-md text-base font-medium text-foreground hover:bg-secondary hover:text-primary'
            onClick={() => setMenuOpen(false)}
          >
            {t.navigation.reality}
          </a>
          <a
            href='#features'
            className='block px-3 py-2 rounded-md text-base font-medium text-foreground hover:bg-secondary hover:text-primary'
            onClick={() => setMenuOpen(false)}
          >
            {t.navigation.features}
          </a>
          <a
            href='#chapters'
            className='block px-3 py-2 rounded-md text-base font-medium text-foreground hover:bg-secondary hover:text-primary'
            onClick={() => setMenuOpen(false)}
          >
            {t.navigation.chapters}
          </a>
          <a
            href='/blog'
            className='block px-3 py-2 rounded-md text-base font-medium text-foreground hover:bg-secondary hover:text-primary'
            onClick={() => setMenuOpen(false)}
          >
            {t.navigation.blog}
          </a>
          {/* <a
            href='#videos'
            className='block px-3 py-2 rounded-md text-base font-medium text-foreground hover:bg-secondary hover:text-primary'
            onClick={() => setMenuOpen(false)}
          >
            {t.navigation.videos || 'Videos'}
          </a> */}
          <div className='px-3 py-2'>
            <Button
              variant="ghost"
              size="sm"
              className="flex items-center gap-1"
              aria-label="Cambiar idioma"
            >
              <Globe className="h-4 w-4" />
              <span className="hidden sm:inline-block">{LOCALE_NAMES[locale]}</span>
            </Button>
          </div>
          <div className='px-3 py-2'>
            <Button asChild className='btn-primary w-full'>
              <a
                href={leanpubUrl}
                target='_blank'
                rel='noopener noreferrer'
                onClick={() => {
                  handleAmazonClick();
                  setMenuOpen(false);
                }}
              >
                {t.all.ctaButton}
              </a>
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
